import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  X,
  FileText,
  Shield,
  CheckCircle,
  Clock,
  BarChart3,
  Globe,
  Briefcase,
  Users,
  ArrowRight,
  LayoutDashboard,
  Home,
  Settings,
  MessageSquare,
  Activity,
  Share2,
  SendHorizontal,
} from 'lucide-react';
import {
  collection,
  onSnapshot,
  query,
  orderBy,
} from 'firebase/firestore';
import { db, appId } from '@/lib/firebase';
import type { AdminView } from './AdminSidebar';

interface AdminData {
  id: string;
  role: 'root' | 'admin' | 'moderator';
  email: string;
  displayName: string;
  photoUrl: string | null;
  isActive: boolean;
  createdAt: unknown;
}

type WebsiteServiceAdminStatus = 'new' | 'in_review' | 'contacted' | 'closed';

interface WebsiteRequestRecord {
  id: string;
  companyName: string;
  adminStatus?: WebsiteServiceAdminStatus;
  createdAt?: { toDate?: () => Date };
}

type ContactMessageAdminStatus = 'new' | 'in_review' | 'contacted' | 'closed';

interface ContactMessageRecord {
  id: string;
  companyName: string;
  adminStatus?: ContactMessageAdminStatus;
  createdAt?: { toDate?: () => Date };
}

interface CMSPageDoc {
  id: string;
  status?: 'draft' | 'published';
  type?: string;
}

interface AdminDashboardProps {
  currentUserId: string;
  currentUserRole: 'root' | 'admin' | 'moderator';
  /** Module-specific access list. Null means root (sees everything). */
  allowedModules?: AdminView[] | null;
  onClose?: () => void;
  showNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
  confirmAction?: (title: string, message: string, action: () => void) => void;
}

export const AdminDashboard = ({
  currentUserRole,
  allowedModules = null,
  onClose,
}: AdminDashboardProps) => {
  const DISABLED_MODULES = new Set<AdminView>(['websiteRequests', 'contactMessages', 'scanRequests']);
  const isDisabledModule = (module: AdminView): boolean => DISABLED_MODULES.has(module);
  const [cmsPages, setCmsPages] = useState<CMSPageDoc[]>([]);
  const [seoCount, setSeoCount] = useState(0);
  const [websiteRequests, setWebsiteRequests] = useState<WebsiteRequestRecord[]>([]);
  const [contactMessages, setContactMessages] = useState<ContactMessageRecord[]>([]);
  const [scanLeadsCount, setScanLeadsCount] = useState(0);
  const [admins, setAdmins] = useState<AdminData[]>([]);
  const [loading, setLoading] = useState(true);

  // True when root OR module is in their allowed list
  const hasAccess = (module: AdminView) =>
    !isDisabledModule(module) &&
    (currentUserRole === 'root' || !allowedModules || allowedModules.includes(module));

  // CMS pages count (public read — always allowed)
  useEffect(() => {
    if (!hasAccess('pages')) {
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, 'artifacts', appId, 'cmsPages'),
      orderBy('updatedAt', 'desc')
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const pages = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as CMSPageDoc[];
        setCmsPages(pages);
        setLoading(false);
      },
      (error) => {
        console.error('[AdminDashboard] Error subscribing to CMS pages:', error);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SEO overrides count (public read)
  useEffect(() => {
    if (!hasAccess('seo')) return;
    const ref = collection(db, 'artifacts', appId, 'seoOverrides');
    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => setSeoCount(snapshot.size),
      (error) => console.error('[AdminDashboard] Error subscribing to SEO overrides:', error)
    );
    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Website service requests (admin-only read)
  useEffect(() => {
    if (!hasAccess('websiteRequests')) return;
    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'websiteServiceRequests'),
      orderBy('createdAt', 'desc')
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const requests = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as WebsiteRequestRecord[];
        setWebsiteRequests(requests);
      },
      (error) => console.error('[AdminDashboard] Error subscribing to website requests:', error)
    );
    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Contact messages (Strategy Call form)
  useEffect(() => {
    if (!hasAccess('contactMessages')) return;
    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'contact_messages'),
      orderBy('createdAt', 'desc')
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const messages = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as ContactMessageRecord[];
        setContactMessages(messages);
      },
      (error) => console.error('[AdminDashboard] Error subscribing to contact_messages:', error)
    );
    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Readiness Scan leads (admin-only read)
  useEffect(() => {
    if (!hasAccess('scanRequests')) return;
    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'leads'),
      orderBy('createdAt', 'desc')
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const count = snapshot.docs.filter(
          (d) => d.data().source === 'footer_federal_readiness_scan'
        ).length;
        setScanLeadsCount(count);
      },
      (error) => console.error('[AdminDashboard] Error subscribing to scan leads:', error)
    );
    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Admins list (root only)
  useEffect(() => {
    if (currentUserRole !== 'root') {
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, 'artifacts', appId, 'admins'),
      orderBy('createdAt', 'asc')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedAdmins = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as AdminData[];
      setAdmins(fetchedAdmins);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [currentUserRole]);

  // Derive stats
  const publishedPages = cmsPages.filter((p) => p.status === 'published').length;
  const draftPages = cmsPages.filter((p) => p.status === 'draft').length;

  const requestsByStatus = {
    new: websiteRequests.filter((r) => (r.adminStatus || 'new') === 'new').length,
    in_review: websiteRequests.filter((r) => r.adminStatus === 'in_review').length,
    contacted: websiteRequests.filter((r) => r.adminStatus === 'contacted').length,
    closed: websiteRequests.filter((r) => r.adminStatus === 'closed').length,
  };
  const totalRequests = websiteRequests.length;
  const pendingRequests = requestsByStatus.new + requestsByStatus.in_review;

  const totalAdmins = admins.length;
  const activeAdmins = admins.filter((a) => a.isActive).length;

  const recentRequests = websiteRequests.slice(0, 5);

  const contactMessagesByStatus = {
    new: contactMessages.filter((r) => (r.adminStatus || 'new') === 'new').length,
    in_review: contactMessages.filter((r) => r.adminStatus === 'in_review').length,
    contacted: contactMessages.filter((r) => r.adminStatus === 'contacted').length,
    closed: contactMessages.filter((r) => r.adminStatus === 'closed').length,
  };
  const totalContactMessages = contactMessages.length;
  const pendingContactMessages =
    contactMessagesByStatus.new + contactMessagesByStatus.in_review;
  const recentContactMessages = contactMessages.slice(0, 5);

  // Visible stat cards — only for accessible modules
  const statCards = [
    hasAccess('pages') && (
      <div key="total-pages" className="bg-blue-50 rounded-lg p-4 border border-blue-100">
        <div className="flex items-center justify-between mb-2">
          <FileText className="text-blue-600" size={24} />
          <span className="text-2xl font-bold text-blue-900">{cmsPages.length}</span>
        </div>
        <p className="text-sm text-blue-700 font-medium">Total CMS Pages</p>
      </div>
    ),
    hasAccess('pages') && (
      <div key="published-pages" className="bg-green-50 rounded-lg p-4 border border-green-100">
        <div className="flex items-center justify-between mb-2">
          <CheckCircle className="text-green-600" size={24} />
          <span className="text-2xl font-bold text-green-900">{publishedPages}</span>
        </div>
        <p className="text-sm text-green-700 font-medium">Published Pages</p>
      </div>
    ),
    hasAccess('pages') && (
      <div key="draft-pages" className="bg-amber-50 rounded-lg p-4 border border-amber-100">
        <div className="flex items-center justify-between mb-2">
          <Clock className="text-amber-600" size={24} />
          <span className="text-2xl font-bold text-amber-900">{draftPages}</span>
        </div>
        <p className="text-sm text-amber-700 font-medium">Draft Pages</p>
      </div>
    ),
    hasAccess('seo') && (
      <div key="seo-overrides" className="bg-purple-50 rounded-lg p-4 border border-purple-100">
        <div className="flex items-center justify-between mb-2">
          <Globe className="text-purple-600" size={24} />
          <span className="text-2xl font-bold text-purple-900">{seoCount}</span>
        </div>
        <p className="text-sm text-purple-700 font-medium">SEO Overrides</p>
      </div>
    ),
    hasAccess('websiteRequests') && (
      <div key="total-requests" className="bg-sky-50 rounded-lg p-4 border border-sky-100">
        <div className="flex items-center justify-between mb-2">
          <Briefcase className="text-sky-600" size={24} />
          <span className="text-2xl font-bold text-sky-900">{totalRequests}</span>
        </div>
        <p className="text-sm text-sky-700 font-medium">Website Requests</p>
      </div>
    ),
    hasAccess('contactMessages') && (
      <div key="contact-messages" className="bg-indigo-50 rounded-lg p-4 border border-indigo-100">
        <div className="flex items-center justify-between mb-2">
          <MessageSquare className="text-indigo-600" size={24} />
          <span className="text-2xl font-bold text-indigo-900">{totalContactMessages}</span>
        </div>
        <p className="text-sm text-indigo-700 font-medium">Strategy Call Messages</p>
      </div>
    ),
    hasAccess('scanRequests') && (
      <div key="scan-requests" className="bg-teal-50 rounded-lg p-4 border border-teal-100">
        <div className="flex items-center justify-between mb-2">
          <Activity className="text-teal-600" size={24} />
          <span className="text-2xl font-bold text-teal-900">{scanLeadsCount}</span>
        </div>
        <p className="text-sm text-teal-700 font-medium">Readiness Scan Requests</p>
      </div>
    ),
  ].filter(Boolean);

  // Visible quick links — only for accessible modules
  type QuickLink = { to: string; icon: React.JSX.Element; label: string };
  const quickLinks: QuickLink[] = ([
    hasAccess('pages')          ? { to: '/admin/pages',             icon: <FileText   className="text-blue-600 shrink-0" size={20} />, label: 'CMS Pages' }          : null,
    hasAccess('home')           ? { to: '/admin/home',              icon: <Home       className="text-blue-600 shrink-0" size={20} />, label: 'Set Home Page' }           : null,
    hasAccess('seo')            ? { to: '/admin/seo',               icon: <Globe      className="text-blue-600 shrink-0" size={20} />, label: 'SEO' }                 : null,
    hasAccess('linkPreview')    ? { to: '/admin/link-preview',      icon: <Share2     className="text-blue-600 shrink-0" size={20} />, label: 'Link Preview' }         : null,
    hasAccess('websiteRequests')? { to: '/admin/website-requests',  icon: <Briefcase  className="text-blue-600 shrink-0" size={20} />, label: 'Website Requests' }    : null,
    hasAccess('contactMessages')? { to: '/admin/contact-messages', icon: <MessageSquare className="text-blue-600 shrink-0" size={20} />, label: 'Strategy Call Messages' } : null,
    hasAccess('monthlyImpactCampaigns') ? { to: '/admin/monthly-impact-campaigns', icon: <SendHorizontal className="text-blue-600 shrink-0" size={20} />, label: 'Monthly Impact Campaigns' } : null,
    hasAccess('scanRequests')   ? { to: '/admin/scan-requests',    icon: <Activity   className="text-blue-600 shrink-0" size={20} />, label: 'Readiness Scan Requests' } : null,
    currentUserRole === 'root'  ? { to: '/admin/users',             icon: <Users      className="text-blue-600 shrink-0" size={20} />, label: 'User Management' }     : null,
    hasAccess('settings')       ? { to: '/admin/settings',          icon: <Settings   className="text-blue-600 shrink-0" size={20} />, label: 'Settings & Branding' } : null,
  ] as (QuickLink | null)[]).filter((item): item is QuickLink => item !== null);

  const formatDate = (timestamp: unknown) => {
    if (!timestamp) return '—';
    const date =
      typeof timestamp === 'object' &&
      timestamp !== null &&
      'toDate' in timestamp &&
      typeof (timestamp as { toDate?: () => Date }).toDate === 'function'
        ? (timestamp as { toDate: () => Date }).toDate()
        : new Date(timestamp as string | number);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const subtitle =
    currentUserRole === 'root' || !allowedModules
      ? 'Overview of content and system statistics for GSGO.'
      : `Your accessible modules: ${allowedModules
          .filter((module) => !isDisabledModule(module))
          .map((m) => m.charAt(0).toUpperCase() + m.slice(1))
          .join(', ')}.`;

  const isModalMode = !!onClose;

  const dashboardContent = (
    <div className={isModalMode ? 'overflow-y-auto p-6' : ''}>
      {loading && currentUserRole === 'root' ? (
        <div className="text-center py-12 text-gray-500">Loading dashboard...</div>
      ) : (
        <div className="space-y-6">
          {/* Subtitle: shown here only in modal; full page renders it under the page title */}
          {isModalMode && <p className="text-gray-600">{subtitle}</p>}

          {/* Key metrics — filtered by module access */}
          {statCards.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {statCards}
            </div>
          )}

          {/* Website requests summary — only for websiteRequests module */}
          {hasAccess('websiteRequests') && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <BarChart3 size={20} />
                  Website Service Requests
                </h3>
                <Link
                  to="/admin/website-requests"
                  className="text-sm text-blue-600 font-medium flex items-center gap-1 hover:underline"
                >
                  Manage requests
                  <ArrowRight size={14} />
                </Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Total</p>
                  <p className="text-xl font-bold text-gray-900">{totalRequests}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">New</p>
                  <p className="text-xl font-bold text-blue-600">{requestsByStatus.new}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">In Review</p>
                  <p className="text-xl font-bold text-amber-600">{requestsByStatus.in_review}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Contacted / Closed</p>
                  <p className="text-xl font-bold text-green-600">
                    {requestsByStatus.contacted + requestsByStatus.closed}
                  </p>
                </div>
              </div>
              {pendingRequests > 0 && (
                <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                  {pendingRequests} request(s) need attention (new or in review).
                </p>
              )}
            </div>
          )}

          {/* Quick links — filtered by module access */}
          {quickLinks.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <LayoutDashboard size={20} />
                Quick links
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {quickLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                  >
                    {link.icon}
                    <span className="font-medium text-gray-900">{link.label}</span>
                    <ArrowRight className="ml-auto text-gray-400" size={18} />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Recent website requests — only for websiteRequests module */}
          {hasAccess('websiteRequests') && (
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <Briefcase size={20} />
                  Recent Website Requests
                </h3>
                <Link
                  to="/admin/website-requests"
                  className="text-sm text-blue-600 font-medium flex items-center gap-1"
                >
                  View all
                  <ArrowRight size={14} />
                </Link>
              </div>
              <div className="space-y-2">
                {recentRequests.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No website requests yet</p>
                ) : (
                  recentRequests.map((req) => (
                    <div
                      key={req.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-100 hover:bg-gray-100 transition-colors"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 truncate">
                          {req.companyName || 'Unknown company'}
                        </p>
                        <p className="text-xs text-gray-500">{formatDate(req.createdAt)}</p>
                      </div>
                      <span
                        className={`shrink-0 text-xs font-semibold px-2 py-1 rounded ${
                          req.adminStatus === 'new'
                            ? 'bg-blue-100 text-blue-700'
                            : req.adminStatus === 'in_review'
                              ? 'bg-amber-100 text-amber-700'
                              : req.adminStatus === 'contacted'
                                ? 'bg-purple-100 text-purple-700'
                                : 'bg-emerald-100 text-emerald-700'
                        }`}
                      >
                        {(req.adminStatus || 'new').replace('_', ' ')}
                      </span>
                      <Link to="/admin/website-requests" className="shrink-0 text-blue-600 ml-2">
                        <ArrowRight size={16} />
                      </Link>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Strategy Call Messages summary — only for contactMessages module */}
          {hasAccess('contactMessages') && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <MessageSquare size={20} />
                  Strategy Call Messages
                </h3>
                <Link
                  to="/admin/contact-messages"
                  className="text-sm text-blue-600 font-medium flex items-center gap-1 hover:underline"
                >
                  Manage messages
                  <ArrowRight size={14} />
                </Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Total</p>
                  <p className="text-xl font-bold text-gray-900">{totalContactMessages}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">New</p>
                  <p className="text-xl font-bold text-blue-600">{contactMessagesByStatus.new}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">In Review</p>
                  <p className="text-xl font-bold text-amber-600">{contactMessagesByStatus.in_review}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Contacted / Closed</p>
                  <p className="text-xl font-bold text-green-600">
                    {contactMessagesByStatus.contacted + contactMessagesByStatus.closed}
                  </p>
                </div>
              </div>
              {pendingContactMessages > 0 && (
                <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                  {pendingContactMessages} message(s) need attention (new or in review).
                </p>
              )}
            </div>
          )}

          {/* Recent Strategy Call messages */}
          {hasAccess('contactMessages') && (
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <MessageSquare size={20} />
                  Recent Strategy Call Messages
                </h3>
                <Link
                  to="/admin/contact-messages"
                  className="text-sm text-blue-600 font-medium flex items-center gap-1"
                >
                  View all
                  <ArrowRight size={14} />
                </Link>
              </div>
              <div className="space-y-2">
                {recentContactMessages.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No strategy call messages yet</p>
                ) : (
                  recentContactMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-100 hover:bg-gray-100 transition-colors"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 truncate">
                          {msg.companyName || 'Unknown company'}
                        </p>
                        <p className="text-xs text-gray-500">{formatDate(msg.createdAt)}</p>
                      </div>
                      <span
                        className={`shrink-0 text-xs font-semibold px-2 py-1 rounded ${
                          msg.adminStatus === 'new'
                            ? 'bg-blue-100 text-blue-700'
                            : msg.adminStatus === 'in_review'
                              ? 'bg-amber-100 text-amber-700'
                              : msg.adminStatus === 'contacted'
                                ? 'bg-purple-100 text-purple-700'
                                : 'bg-emerald-100 text-emerald-700'
                        }`}
                      >
                        {(msg.adminStatus || 'new').replace('_', ' ')}
                      </span>
                      <Link to="/admin/contact-messages" className="shrink-0 text-blue-600 ml-2">
                        <ArrowRight size={16} />
                      </Link>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Admin statistics (root only) */}
          {currentUserRole === 'root' && (
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Shield size={20} />
                Admin statistics
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Total admins</p>
                  <p className="text-2xl font-bold text-gray-900">{totalAdmins}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Active admins</p>
                  <p className="text-2xl font-bold text-green-600">{activeAdmins}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (isModalMode) {
    return (
      <>
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full my-8 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 shrink-0">
              <div className="flex items-center gap-2">
                <Shield className="text-blue-600" size={24} />
                <h2 className="text-2xl font-bold text-gray-900">Admin Dashboard</h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            {dashboardContent}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="w-full">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="text-blue-600" size={28} />
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          </div>
          <p className="text-gray-600">{subtitle}</p>
        </div>
        {dashboardContent}
      </div>
    </>
  );
};
